import * as THREE from "three";

export type GradientStop = { 
  stop: number; 
  color: string | THREE.Color; 
  alpha?: number 
};

export class VfxGradientAtlas 
{
  private static ENABLE_LOGGING: boolean = false;

  public static readonly WIDTH = 256;
  public static readonly HEIGHT = 64;

  private static canvas: HTMLCanvasElement;
  private static ctx: CanvasRenderingContext2D;
  private static texture: THREE.CanvasTexture;
  private static currentRow: number = 0;
  private static cache: Map<string, number> = new Map();

  public static init(): void 
  {
    if ( this.texture ) return;

    this.canvas = document.createElement( "canvas" );
    this.canvas.width = this.WIDTH;
    this.canvas.height = this.HEIGHT;
    this.ctx = this.canvas.getContext( "2d" )!;
    
    // Очищаємо прозорим чорним
    this.ctx.clearRect( 0, 0, this.WIDTH, this.HEIGHT );
    
    this.texture = new THREE.CanvasTexture( this.canvas );
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.generateMipmaps = false;

    if ( this.ENABLE_LOGGING )
      this.logAtlasStats();
  }

  public static getTexture(): THREE.Texture 
  {
    if ( !this.texture ) this.init();
    return this.texture;
  }

  public static registerGradient( stops: GradientStop[] ): number 
  {
    const fingerprint = stops
      .map( s => {
        const hex = typeof s.color === "string"
          ? s.color.toLowerCase()
          : "#" + s.color.getHexString().toLowerCase();
        const alpha = s.alpha !== undefined ? s.alpha.toFixed(3) : "1.000";
        return `${s.stop.toFixed(3)}:${hex}:${alpha}`;
      } )
      .join( "|" );

    if ( this.cache.has( fingerprint ) )
    {
      return this.cache.get( fingerprint )!;
    }

    if ( !this.texture ) this.init();

    if ( this.currentRow >= this.HEIGHT ) 
    {
      console.warn( "VfxGradientAtlas is full! Maximum 64 gradients allowed." );
      return 0.0;
    }

    const y = this.currentRow;
    const grad = this.ctx.createLinearGradient( 0, 0, this.WIDTH, 0 );

    for ( const s of stops ) 
    {
      let cssColor = "";
      if ( typeof s.color === "string" ) 
      {
        cssColor = s.color;
      } 
      else 
      {
        const r = Math.min( 255, Math.max( 0, Math.floor( s.color.r * 255 ) ) );
        const g = Math.min( 255, Math.max( 0, Math.floor( s.color.g * 255 ) ) );
        const b = Math.min( 255, Math.max( 0, Math.floor( s.color.b * 255 ) ) );
        const a = s.alpha !== undefined ? Math.min( 1.0, Math.max( 0.0, s.alpha ) ) : 1.0;
        cssColor = `rgba(${r},${g},${b},${a})`;
      }
      grad.addColorStop( Math.min( 1.0, Math.max( 0.0, s.stop ) ), cssColor );
    }

    this.ctx.fillStyle = grad;
    this.ctx.fillRect( 0, y, this.WIDTH, 1 );

    this.texture.needsUpdate = true;
    this.currentRow++;

    if ( this.ENABLE_LOGGING )
      this.logAtlasStats( y, stops );

    // У Three.js CanvasTexture за замовчуванням flipY = true, тому рядок y з canvas (0 зверху)
    // на GPU відповідає координаті V = 1.0 - (y + 0.5) / HEIGHT
    const v = 1.0 - ( y + 0.5 ) / this.HEIGHT;
    this.cache.set( fingerprint, v );
    return v;
  }

  public static logAtlasStats( registered_row?: number, stops?: GradientStop[] ): void
  {
    const rows_used = this.currentRow;
    const rows_total = this.HEIGHT;
    const free_rows = rows_total - rows_used;
    const free_percent = ( ( free_rows / rows_total ) * 100 ).toFixed( 1 );
    const memory_bytes = this.WIDTH * this.HEIGHT * 4;

    let detail = "";
    if ( registered_row !== undefined && stops )
    {
      const row_v = ( 1.0 - ( registered_row + 0.5 ) / this.HEIGHT ).toFixed( 4 );
      const stops_desc = stops
        .map( s => {
          const color_str = typeof s.color === "string" ? s.color : "#" + s.color.getHexString();
          return `${s.stop} -> ${color_str}${s.alpha !== undefined ? ` (a: ${s.alpha})` : ""}`;
        } )
        .join( ", " );
      detail = `  • Registered Row: #${registered_row} (V: ${row_v})\n  • Stops (${stops.length}): [${stops_desc}]\n`;
    }

    console.log(
      `[VfxGradientAtlas] Atlas compiled (${this.WIDTH}x${this.HEIGHT} px, ${( memory_bytes / 1024 ).toFixed( 1 )} KB):\n` +
      detail +
      `  • Rows Used: ${rows_used}/${rows_total} (${free_rows} free, ${free_percent}% free)`
    );
  }

  public static getStats(): {
    width: number;
    height: number;
    rows_used: number;
    rows_total: number;
    memory_bytes: number;
  }
  {
    return {
      width: this.WIDTH,
      height: this.HEIGHT,
      rows_used: this.currentRow,
      rows_total: this.HEIGHT,
      memory_bytes: this.WIDTH * this.HEIGHT * 4,
    };
  }
}
